import { Button, type ButtonProps, Icon, type NumericSize, cn, useUi } from '@glade-ai/ui';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { UsersAvatarGroup, type UsersAvatarGroupUser } from '@/containers/UsersAvatarGroup';

/** Props for {@link OpenThreadButton}. */
export type OpenThreadButtonProps = {
  /** Thread participant info for the avatar group. */
  participants: UsersAvatarGroupUser[];
  /** Number of replies in the thread. When omitted or zero the button shows only the "View thread" CTA. */
  replyCount?: number;
  /** Callback when the button is clicked. */
  onClick: () => void;
  /** Avatar size passed to UsersAvatarGroup. @default 3 */
  avatarSize?: NumericSize;
} & Omit<ButtonProps, 'children'>;

/** Full-width ghost button that shows thread participants, reply count, and a "View thread" CTA. */
const OpenThreadButton: FC<OpenThreadButtonProps> = ({
  participants,
  replyCount,
  avatarSize = 3,
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { supportsHover } = useUi();
  const hasReplies = replyCount != null && replyCount > 0;

  return (
    <Button
      variant='ghost'
      size='sm'
      {...props}
      className={cn(
        'w-full h-auto ps-1.5 py-1.5 filter-none border border-transparent hover:border-border hover:bg-background focus:bg-background group/button',
        className,
      )}
    >
      {hasReplies ? (
        <>
          {participants.length > 0 && <UsersAvatarGroup users={participants} size={avatarSize} noLabel />}
          <span className='text-[var(--color-primary)] font-semibold text-xs grow text-start'>
            {t('chat.replyCount', { count: replyCount })}
          </span>
          <span
            className={cn(
              'gl-description flex flex-row items-center gap-2 transition-opacity',
              supportsHover
                ? 'opacity-0 group-hover/button:opacity-100 group-focus-visible/button:opacity-100'
                : 'opacity-100',
            )}
          >
            <span>View thread</span>
            <Icon icon='ph--arrow-right--regular' size={4} />
          </span>
        </>
      ) : (
        <span className='text-[var(--color-primary)] text-xs font-semibold flex flex-row items-center gap-2 grow'>
          <span>View thread</span>
          <Icon icon='ph--arrow-right--regular' size={4} />
        </span>
      )}
    </Button>
  );
};

export { OpenThreadButton };
